import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GearSix, PaperPlaneTilt, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { Switch } from "@/components/ui/switch";

const META = { ses: { label: "Amazon SES", color: "#F59E0B" }, resend: { label: "Resend", color: "#6E3FFC" } };

function statusLine(key, st) {
  if (key === "ses") {
    if (!st.configured) return { ok: false, text: "AWS keys not configured" };
    if (st.ok === false) return { ok: false, text: st.error };
    return { ok: true, text: `Region ${st.region} · ${st.sent_24h.toLocaleString()} / ${st.max_24h.toLocaleString()} sent in 24h · ${st.max_rate}/s` };
  }
  return st.configured ? { ok: true, text: "API key configured" } : { ok: false, text: "RESEND_API_KEY not configured" };
}

function ProviderRow({ pkey, st, isPrimary, onPrimary, to }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const line = statusLine(pkey, st);
  const test = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/email-provider/test", { provider: pkey, to: to || null });
      setResult(data);
      data.ok ? toast.success(`Test sent via ${META[pkey].label} to ${data.to}`) : toast.error(`${META[pkey].label} test failed`);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Test failed";
      setResult({ ok: false, error: msg }); toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`provider-row-${pkey}`} className={`rounded-2xl border p-4 transition-colors ${isPrimary ? "border-riq-primary bg-riq-primary/5" : "border-riq-border"}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="h-3 w-3 rounded-full" style={{ background: META[pkey].color }} />
        <span className="font-semibold text-neutral-800">{META[pkey].label}</span>
        {isPrimary && <span data-testid={`provider-primary-badge-${pkey}`} className="rounded-full bg-riq-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Primary</span>}
        <div className="ml-auto flex items-center gap-2">
          {!isPrimary && (
            <button data-testid={`provider-make-primary-${pkey}`} onClick={() => onPrimary(pkey)}
              className="rounded-full border border-riq-border px-3 py-1 text-xs font-medium hover:border-riq-primary hover:text-riq-primary transition-colors">Make primary</button>
          )}
          <button data-testid={`provider-test-${pkey}`} onClick={test} disabled={busy}
            className="flex items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1 text-xs font-medium text-white hover:bg-riq-primary transition-colors disabled:opacity-60">
            {busy ? <CircleNotch size={13} className="animate-spin" /> : <PaperPlaneTilt size={13} weight="bold" />} Send test
          </button>
        </div>
      </div>
      <div data-testid={`provider-status-${pkey}`} className={`mt-2 flex items-start gap-1.5 text-xs ${line.ok ? "text-emerald-700" : "text-red-600"}`}>
        {line.ok ? <CheckCircle size={14} weight="fill" className="shrink-0 mt-px" /> : <XCircle size={14} weight="fill" className="shrink-0 mt-px" />}
        <span className="break-all">{line.text}</span>
      </div>
      {result && (
        <div data-testid={`provider-test-result-${pkey}`} className={`mt-2 rounded-xl px-3 py-2 text-xs ${result.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
          {result.ok ? `Delivered to ${result.to} — check the inbox.` : result.error}
        </div>
      )}
    </div>
  );
}

export const EmailProviderSettings = () => {
  const [data, setData] = useState(null);
  const [to, setTo] = useState("");
  const load = () => api.get("/admin/email-provider").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const save = async (cfg) => {
    const prev = data.config;
    setData({ ...data, config: cfg });
    try {
      await api.put("/admin/email-provider", cfg);
      toast.success(`Primary: ${META[cfg.primary].label} · failover ${cfg.failover ? "on" : "off"}`);
    } catch {
      setData({ ...data, config: prev }); toast.error("Could not save provider settings");
    }
  };

  if (data === false) return <Card><div className="py-4 text-center text-sm text-muted-foreground">Could not load provider settings.</div></Card>;
  if (!data) return <Loading />;
  const { config, status } = data;
  const other = config.primary === "ses" ? "resend" : "ses";

  return (
    <Card data-testid="email-provider-settings">
      <div className="flex items-center gap-2 mb-1">
        <GearSix size={18} weight="duotone" className="text-riq-primary" />
        <div className="text-sm font-semibold text-neutral-700">Email providers</div>
        <span className="ml-auto text-xs text-muted-foreground truncate">From: {status.sender}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">All outgoing email uses the primary provider. Tests always go only through the provider you pick.</p>
      <div className="space-y-3">
        {["ses", "resend"].map((k) => (
          <ProviderRow key={k} pkey={k} st={status[k]} isPrimary={config.primary === k} to={to}
            onPrimary={(p) => save({ ...config, primary: p })} />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-riq-border px-4 py-3">
        <div>
          <div className="text-sm font-medium text-neutral-800">Fail over to {META[other].label}</div>
          <div className="text-xs text-muted-foreground">If {META[config.primary].label} errors, retry the email through {META[other].label}.</div>
        </div>
        <Switch data-testid="provider-failover-switch" checked={config.failover} onCheckedChange={(v) => save({ ...config, failover: v })} />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Send tests to</label>
        <input data-testid="provider-test-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder={data.admin_email}
          className="mt-1 w-full rounded-xl border border-riq-border px-3 py-2 text-sm outline-none focus:border-riq-primary" />
      </div>
    </Card>
  );
};
